import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ValidationErrors from '@/components/validation-errors';
import { cn } from '@/lib/utils';
import type { Project } from '@/types/project';
import { Form } from '@inertiajs/react';
import { useState } from 'react';

interface Props {
  project: Project;
  action: string;
}

export default function StatusForm({ project, action }: Props) {
  const [editing, setEditing] = useState(false);

  if (!editing) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatusField label="Now" value={project.statusNow} />
          <StatusField label="Next" value={project.statusNext} />
          <StatusField
            label="Blocked"
            value={project.statusBlocked}
            tone={project.statusBlocked ? 'danger' : 'default'}
          />
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>
          Update status
        </Button>
      </div>
    );
  }

  return (
    <Form
      method="patch"
      action={action}
      className="space-y-4"
      onSuccess={() => setEditing(false)}
    >
      {({ errors, processing }) => (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="status_now">Now</Label>
              <Textarea
                id="status_now"
                name="status[now]"
                defaultValue={project.statusNow ?? ''}
                rows={3}
                autoFocus
              />
              <ValidationErrors errors={errors.now} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status_next">Next</Label>
              <Textarea
                id="status_next"
                name="status[next]"
                defaultValue={project.statusNext ?? ''}
                rows={3}
              />
              <ValidationErrors errors={errors.next} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status_blocked">Blocked</Label>
              <Textarea
                id="status_blocked"
                name="status[blocked]"
                defaultValue={project.statusBlocked ?? ''}
                rows={3}
              />
              <ValidationErrors errors={errors.blocked} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={processing}>
              Save status
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              disabled={processing}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </Form>
  );
}

function StatusField({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string | null;
  tone?: 'default' | 'danger';
}) {
  const empty = !value?.trim();

  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{label}</p>
      <p
        className={cn(
          'text-sm whitespace-pre-wrap',
          empty && 'text-muted-foreground italic',
          tone === 'danger' && !empty && 'text-destructive'
        )}
      >
        {empty ? '—' : value}
      </p>
    </div>
  );
}
